import {
  Button,
  Checkbox,
  DateField,
  Dialog,
  Select,
  TextInput,
} from "@hisani/ui";
import { Search, SlidersHorizontal, X } from "lucide-react";
import { useTranslations } from "next-intl";
import React, { useMemo } from "react";
import type {
  PlannerFilters,
  PlannerLocale,
  PlannerPlaces,
} from "./planner-contracts";
import { plannerDateLabel } from "./planner-formatting";

type PlannerFilterProps = {
  readonly filters: PlannerFilters;
  readonly places: PlannerPlaces | null;
  readonly locale: PlannerLocale;
  readonly onChange: (filters: PlannerFilters) => void;
  readonly onSearch: () => void;
  readonly onClear: () => void;
};

export function hasPlannerFilters(filters: PlannerFilters): boolean {
  return Object.values(filters).some((value) => value !== "");
}

export function PlannerActiveFilters({
  filters,
  places,
  locale,
  onClear,
  onRemove,
}: Pick<PlannerFilterProps, "filters" | "places" | "locale" | "onClear"> & {
  readonly onRemove: (field: keyof PlannerFilters) => void;
}) {
  const t = useTranslations("Planner");
  const jurisdiction = places?.jurisdictions.find(
    (item) => item.code === filters.jurisdiction,
  );
  const racecourse = places?.racecourses.find(
    (item) => item.code === filters.racecourse,
  );
  const localizedName = (item: {
    readonly name: string;
    readonly nameAr: string;
  }) => (locale === "ar" ? item.nameAr : item.name);
  const numberLabel = (value: string) => Number(value).toLocaleString(locale);
  const labels = (
    [
      [
        "breed",
        filters.breed === "THOROUGHBRED"
          ? t("thoroughbred")
          : filters.breed === "PUREBRED_ARABIAN"
            ? t("purebredArabian")
            : filters.breed === "OPEN"
              ? t("open")
              : filters.breed === "OTHER"
                ? t("other")
                : "",
      ],
      [
        "jurisdiction",
        filters.jurisdiction === ""
          ? ""
          : jurisdiction === undefined
            ? filters.jurisdiction
            : localizedName(jurisdiction),
      ],
      [
        "racecourse",
        filters.racecourse === ""
          ? ""
          : racecourse === undefined
            ? filters.racecourse
            : localizedName(racecourse),
      ],
      [
        "dateFrom",
        filters.dateFrom === ""
          ? ""
          : `${t("from")}: ${plannerDateLabel(filters.dateFrom, locale)}`,
      ],
      [
        "dateTo",
        filters.dateTo === ""
          ? ""
          : `${t("to")}: ${plannerDateLabel(filters.dateTo, locale)}`,
      ],
      [
        "surface",
        filters.surface === "DIRT"
          ? t("dirt")
          : filters.surface === "TURF"
            ? t("turf")
            : filters.surface === "ALL_WEATHER"
              ? t("allWeather")
              : "",
      ],
      [
        "distanceFrom",
        filters.distanceFrom === ""
          ? ""
          : `${t("minimumDistance")}: ${numberLabel(filters.distanceFrom)} ${t("metres")}`,
      ],
      [
        "distanceTo",
        filters.distanceTo === ""
          ? ""
          : `${t("maximumDistance")}: ${numberLabel(filters.distanceTo)} ${t("metres")}`,
      ],
      ["grade", filters.grade],
      ["category", filters.category],
      ["ageRestriction", filters.ageRestriction],
      ["sexRestriction", filters.sexRestriction],
      [
        "handicapRating",
        filters.handicapRating === ""
          ? ""
          : `${t("handicapRating")}: ${numberLabel(filters.handicapRating)}`,
      ],
      [
        "benchmarkRating",
        filters.benchmarkRating === ""
          ? ""
          : `${t("benchmarkRating")}: ${numberLabel(filters.benchmarkRating)}`,
      ],
      [
        "ratedMinimum",
        filters.ratedMinimum === ""
          ? ""
          : `${t("ratedMinimum")}: ${numberLabel(filters.ratedMinimum)}`,
      ],
      ["uaeBredOnly", filters.uaeBredOnly === "" ? "" : t("uaeBredOnly")],
      [
        "privateOwnersOnly",
        filters.privateOwnersOnly === "" ? "" : t("privateOwnersOnly"),
      ],
      [
        "invitationOnly",
        filters.invitationOnly === "" ? "" : t("invitationOnly"),
      ],
      ["season", filters.season],
    ] satisfies readonly (readonly [keyof PlannerFilters, string])[]
  ).filter((entry) => entry[1] !== "");

  if (labels.length === 0) return null;

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <div
        className="flex min-w-0 flex-wrap items-center gap-2"
        role="list"
        aria-label={t("filters")}
      >
        {labels.map(([field, label]) => (
          <span key={field} role="listitem" className="max-w-full">
            <button
              type="button"
              className="flex min-h-11 max-w-full items-center rounded-full px-0 text-xs font-semibold text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
              aria-label={t("removeFilter", { filter: label })}
              onClick={() => onRemove(field)}
            >
              <span className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-surface px-3 py-1.5">
                <span className="break-words">{label}</span>
                <X className="size-3.5 shrink-0" aria-hidden="true" />
              </span>
            </button>
          </span>
        ))}
      </div>
      {labels.length === 0 ? null : (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="min-h-11 shrink-0 px-2 text-xs text-brand-strong"
          onClick={onClear}
        >
          {t("clear")}
        </Button>
      )}
    </div>
  );
}

export function PlannerMobileFilters({
  open,
  disabled,
  onOpen,
  onClose,
  ...filterProps
}: PlannerFilterProps & {
  readonly open: boolean;
  readonly disabled: boolean;
  readonly onOpen: () => void;
  readonly onClose: () => void;
}) {
  const t = useTranslations("Planner");
  const hasDraftFilters = hasPlannerFilters(filterProps.filters);
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="lg"
        className="size-11 min-h-11 shrink-0 p-0"
        onClick={onOpen}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t("mobileFilters")}
      >
        <SlidersHorizontal className="size-5" aria-hidden="true" />
      </Button>
      <Dialog
        open={open}
        title={t("filters")}
        description={t("filterDescription")}
        closeLabel={t("closeFilters")}
        mobileDensity="compact"
        onClose={onClose}
        footer={
          <>
            <Button
              type="button"
              onClick={filterProps.onSearch}
              variant="accent"
              size="lg"
              className="min-h-12 gap-2"
            >
              <Search className="size-4" aria-hidden="true" />
              {t("search")}
            </Button>
            {hasDraftFilters ? (
              <Button
                type="button"
                onClick={filterProps.onClear}
                variant="secondary"
                size="lg"
                className="min-h-12"
              >
                {t("clear")}
              </Button>
            ) : null}
          </>
        }
      >
        <PlannerFilterFields {...filterProps} />
      </Dialog>
    </>
  );
}

function PlannerFilterFields({
  filters,
  places,
  locale,
  onChange,
}: PlannerFilterProps) {
  const t = useTranslations("Planner");
  const visibleCourses = useMemo(
    () =>
      places?.racecourses.filter((course) => {
        if (filters.jurisdiction === "") return false;
        const jurisdiction = places.jurisdictions.find(
          (item) => item.code === filters.jurisdiction,
        );
        return course.jurisdictionId === jurisdiction?.id;
      }) ?? [],
    [filters.jurisdiction, places],
  );

  return (
    <div className="grid gap-2 sm:gap-4">
      <FilterSelect
        label={t("breed")}
        value={filters.breed}
        onChange={(breed) => onChange({ ...filters, breed })}
        options={[
          { value: "OPEN", label: t("open") },
          { value: "THOROUGHBRED", label: t("thoroughbred") },
          { value: "PUREBRED_ARABIAN", label: t("purebredArabian") },
          { value: "OTHER", label: t("other") },
        ]}
        all={t("any")}
      />
      <FilterSelect
        label={t("jurisdiction")}
        value={filters.jurisdiction}
        onChange={(jurisdiction) =>
          onChange({ ...filters, jurisdiction, racecourse: "" })
        }
        options={
          places?.jurisdictions.map((item) => ({
            value: item.code,
            label: locale === "ar" ? item.nameAr : item.name,
          })) ?? []
        }
        all={t("all")}
      />
      <FilterSelect
        label={t("course")}
        value={filters.racecourse}
        onChange={(racecourse) => onChange({ ...filters, racecourse })}
        options={visibleCourses.map((item) => ({
          value: item.code,
          label: locale === "ar" ? item.nameAr : item.name,
        }))}
        all={
          filters.jurisdiction === "" ? t("selectJurisdictionFirst") : t("all")
        }
        disabled={filters.jurisdiction === ""}
      />
      <div className="grid grid-cols-2 gap-3">
        <FilterDate
          label={t("from")}
          locale={locale}
          placeholder={t("datePlaceholder")}
          value={filters.dateFrom}
          onChange={(dateFrom) => onChange({ ...filters, dateFrom })}
        />
        <FilterDate
          label={t("to")}
          locale={locale}
          placeholder={t("datePlaceholder")}
          value={filters.dateTo}
          onChange={(dateTo) => onChange({ ...filters, dateTo })}
        />
      </div>
      <FilterSelect
        label={t("surface")}
        value={filters.surface}
        onChange={(surface) => onChange({ ...filters, surface })}
        options={[
          { value: "DIRT", label: t("dirt") },
          { value: "TURF", label: t("turf") },
          { value: "ALL_WEATHER", label: t("allWeather") },
        ]}
        all={t("all")}
      />
      <DistanceRange
        filters={filters}
        places={places}
        locale={locale}
        onChange={onChange}
      />
      <FilterSelect
        label={t("grade")}
        value={filters.grade}
        onChange={(grade) => onChange({ ...filters, grade })}
        options={
          places?.filters.grades.map((grade) => ({
            value: grade,
            label: grade,
          })) ?? []
        }
        all={t("any")}
      />
      <FilterSelect
        label={t("category")}
        value={filters.category}
        onChange={(category) => onChange({ ...filters, category })}
        options={
          places?.filters.categories.map((category) => ({
            value: category,
            label: category,
          })) ?? []
        }
        all={t("any")}
      />
      <FilterSelect
        label={t("ageRestriction")}
        value={filters.ageRestriction}
        onChange={(ageRestriction) => onChange({ ...filters, ageRestriction })}
        options={
          places?.filters.ageRestrictions.map((restriction) => ({
            value: restriction,
            label: restriction,
          })) ?? []
        }
        all={t("any")}
      />
      <FilterSelect
        label={t("sexRestriction")}
        value={filters.sexRestriction}
        onChange={(sexRestriction) => onChange({ ...filters, sexRestriction })}
        options={
          places?.filters.sexRestrictions.map((restriction) => ({
            value: restriction,
            label: restriction,
          })) ?? []
        }
        all={t("any")}
      />
      <label className="block text-sm font-bold">
        {t("handicapRating")}
        <TextInput
          className="mt-1 sm:mt-2"
          type="number"
          inputMode="numeric"
          min={0}
          max={200}
          value={filters.handicapRating}
          onChange={(event) =>
            onChange({ ...filters, handicapRating: event.target.value })
          }
        />
      </label>
      <FilterSelect
        label={t("benchmarkRating")}
        value={filters.benchmarkRating}
        onChange={(benchmarkRating) =>
          onChange({ ...filters, benchmarkRating })
        }
        options={
          places?.filters.benchmarkRatings.map((rating) => ({
            value: String(rating),
            label: String(rating),
          })) ?? []
        }
        all={t("any")}
      />
      <FilterSelect
        label={t("ratedMinimum")}
        value={filters.ratedMinimum}
        onChange={(ratedMinimum) => onChange({ ...filters, ratedMinimum })}
        options={
          places?.filters.ratedMinimums.map((rating) => ({
            value: String(rating),
            label: String(rating),
          })) ?? []
        }
        all={t("any")}
      />
      <div className="grid gap-1">
        <Checkbox
          checked={filters.uaeBredOnly === "true"}
          onChange={(event) =>
            onChange({
              ...filters,
              uaeBredOnly: event.target.checked ? "true" : "",
            })
          }
        >
          {t("uaeBredOnly")}
        </Checkbox>
        <Checkbox
          checked={filters.privateOwnersOnly === "true"}
          onChange={(event) =>
            onChange({
              ...filters,
              privateOwnersOnly: event.target.checked ? "true" : "",
            })
          }
        >
          {t("privateOwnersOnly")}
        </Checkbox>
        <Checkbox
          checked={filters.invitationOnly === "true"}
          onChange={(event) =>
            onChange({
              ...filters,
              invitationOnly: event.target.checked ? "true" : "",
            })
          }
        >
          {t("invitationOnly")}
        </Checkbox>
      </div>
      <FilterSelect
        label={t("season")}
        value={filters.season}
        onChange={(season) => onChange({ ...filters, season })}
        options={
          places?.filters.seasons.map((season) => ({
            value: season,
            label: season,
          })) ?? []
        }
        all={t("any")}
      />
    </div>
  );
}

function DistanceRange({
  filters,
  places,
  locale,
  onChange,
}: Pick<PlannerFilterProps, "filters" | "places" | "locale" | "onChange">) {
  const t = useTranslations("Planner");
  const minimum = places?.filters.minimumDistanceMeters ?? null;
  const maximum = places?.filters.maximumDistanceMeters ?? null;
  const options = distanceOptions(minimum, maximum);
  const minimumIndex = distanceOptionIndex(
    options,
    filters.distanceFrom === "" ? minimum : Number(filters.distanceFrom),
  );
  const maximumIndex = distanceOptionIndex(
    options,
    filters.distanceTo === "" ? maximum : Number(filters.distanceTo),
  );
  const selectedMinimum = options[minimumIndex] ?? null;
  const selectedMaximum = options[maximumIndex] ?? null;
  const range = Math.max(options.length - 1, 1);
  const start = (minimumIndex / range) * 100;
  const end = (maximumIndex / range) * 100;
  const inputClassName =
    "pointer-events-none absolute inset-0 z-10 h-11 w-full appearance-none bg-transparent focus-visible:outline-none [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:size-6 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-brand-strong [&::-moz-range-thumb]:bg-surface [&::-moz-range-track]:bg-transparent [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:size-6 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-brand-strong [&::-webkit-slider-thumb]:bg-surface";
  const formatDistance = (value: number | null) =>
    value === null
      ? t("notAvailable")
      : `${value.toLocaleString(locale)} ${t("metres")}`;

  return (
    <fieldset
      className="grid gap-2"
      disabled={minimum === null || maximum === null}
    >
      <legend className="text-sm font-bold">{t("distanceRange")}</legend>
      <div className="flex justify-between gap-3 text-xs text-secondary">
        <span>
          {t("minimumDistance")}: {formatDistance(selectedMinimum)}
        </span>
        <span className="text-end">
          {t("maximumDistance")}: {formatDistance(selectedMaximum)}
        </span>
      </div>
      <div className="relative h-11 focus-within:rounded-md focus-within:ring-2 focus-within:ring-focus/20">
        <span className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-border-default" />
        {minimum === null || maximum === null ? null : (
          <span
            className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-brand-strong"
            style={{
              insetInlineStart: `${start}%`,
              insetInlineEnd: `${100 - end}%`,
            }}
          />
        )}
        {minimum === null || maximum === null ? null : (
          <>
            <input
              type="range"
              aria-label={t("minimumDistance")}
              aria-valuetext={formatDistance(selectedMinimum)}
              min={0}
              max={options.length - 1}
              step={1}
              value={minimumIndex}
              className={inputClassName}
              onChange={(event) => {
                const index = Math.min(
                  Number(event.target.value),
                  maximumIndex,
                );
                const distanceFrom = options[index] ?? minimum;
                onChange({ ...filters, distanceFrom: String(distanceFrom) });
              }}
            />
            <input
              type="range"
              aria-label={t("maximumDistance")}
              aria-valuetext={formatDistance(selectedMaximum)}
              min={0}
              max={options.length - 1}
              step={1}
              value={maximumIndex}
              className={`${inputClassName} ${minimumIndex === options.length - 1 ? "z-20" : ""}`}
              onChange={(event) => {
                const index = Math.max(
                  Number(event.target.value),
                  minimumIndex,
                );
                const distanceTo = options[index] ?? maximum;
                onChange({ ...filters, distanceTo: String(distanceTo) });
              }}
            />
          </>
        )}
      </div>
    </fieldset>
  );
}

function distanceOptions(minimum: number | null, maximum: number | null) {
  if (minimum === null || maximum === null) return [];
  const options: number[] = [];
  for (let distance = minimum; distance <= maximum; distance += 200) {
    options.push(distance);
  }
  if (options.at(-1) !== maximum) options.push(maximum);
  return options;
}

function distanceOptionIndex(
  options: readonly number[],
  selected: number | null,
) {
  if (options.length === 0 || selected === null) return 0;
  const exact = options.indexOf(selected);
  if (exact >= 0) return exact;
  return options.reduce((nearest, value, index) => {
    const nearestValue = options[nearest] ?? selected;
    return Math.abs(value - selected) < Math.abs(nearestValue - selected)
      ? index
      : nearest;
  }, 0);
}

function FilterDate({
  label,
  value,
  onChange,
  locale,
  placeholder,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly locale: PlannerLocale;
  readonly placeholder: string;
}) {
  return (
    <label className="block text-sm font-bold">
      {label}
      <DateField
        aria-label={label}
        className="mt-1 sm:mt-2"
        formatDisplayValue={(date) => plannerDateLabel(date, locale)}
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
  all,
  disabled = false,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly {
    readonly value: string;
    readonly label: string;
  }[];
  readonly all: string;
  readonly disabled?: boolean;
}) {
  return (
    <label className="block text-sm font-bold">
      {label}
      <Select
        className="mt-1 sm:mt-2"
        value={value}
        onValueChange={onChange}
        disabled={disabled}
      >
        <option value="">{all}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Select>
    </label>
  );
}
